import {useState} from 'react';
import type {UnitEconomics} from '@ozon-ai/ozon/contracts';
import {Card, CardContent, CardFooter} from '@/components/ui/card.tsx';
import {StoreOtherAccrualsDialog} from '../../store-economics-page/store-other-accruals-dialog';
import {formatUnits} from '../format-money';
import {FlowColumns} from './flow-columns';
import {FlowLine} from './flow-line';
import {netBillableUnits, scaleAmount} from './unit-economics-amount';
import {UnitEconomicsRadialChart} from './unit-economics-radial-chart';
import {UnitStat} from './unit-stat';

type UnitEconomicsWaterfallProps = {
	row: UnitEconomics;
	showPerUnit?: boolean;
	reportingPeriod?: {from: string; to: string};
};

export function UnitEconomicsWaterfall({
	row,
	showPerUnit = true,
	reportingPeriod,
}: UnitEconomicsWaterfallProps) {
	const [otherAccrualsOpen, setOtherAccrualsOpen] = useState(false);
	const netUnits = netBillableUnits(row);
	const perUnit = (value: number | null) => scaleAmount(value, 'perUnit', netUnits);

	return (
		<>
		<Card className="gap-0 overflow-hidden py-0 shadow-sm">
			<div className="flex flex-col gap-4 bg-muted/50 p-3">
				<div className="flex gap-2">
					<UnitStat label="Отгружено" value={formatUnits(row.shippedUnits)}/>
					<UnitStat label="Доставлено" value={formatUnits(row.deliveredUnits)}/>
					<UnitStat label="Возвращено" value={formatUnits(row.returnedUnits)}/>
				</div>
				<UnitEconomicsRadialChart row={row} periodTotalsOnly={!showPerUnit}/>
			</div>

			<CardContent className="flex flex-col gap-1 p-3">
				<FlowColumns showPerUnit={showPerUnit}/>
				<FlowLine
					label="Выручка"
					value={row.sales.revenue}
					unitValue={perUnit(row.sales.revenue)}
					showPerUnit={showPerUnit}
				/>
				<FlowLine
					label="Баллы за скидки"
					value={row.sales.discountPoints}
					unitValue={perUnit(row.sales.discountPoints)}
					showPerUnit={showPerUnit}
				/>
				<FlowLine
					label="Программы партнёров"
					value={row.sales.partnerPrograms}
					unitValue={perUnit(row.sales.partnerPrograms)}
					showPerUnit={showPerUnit}
				/>
				<FlowLine
					label="Итого"
					value={row.sales.total}
					unitValue={perUnit(row.sales.total)}
					total
					showPerUnit={showPerUnit}
				/>
				<FlowLine
					label="Комиссия площадки"
					value={row.reward}
					unitValue={perUnit(row.reward)}
					showPerUnit={showPerUnit}
				/>
				<FlowLine
					label="Эквайринг"
					value={row.acquiring}
					unitValue={perUnit(row.acquiring)}
					showPerUnit={showPerUnit}
				/>
				<FlowLine
					label="Логистика"
					value={row.delivery.logistics}
					unitValue={perUnit(row.delivery.logistics)}
					showPerUnit={showPerUnit}
				/>
				<FlowLine
					label="Доставка до места выдачи"
					value={row.delivery.lastMile}
					unitValue={perUnit(row.delivery.lastMile)}
					showPerUnit={showPerUnit}
				/>
				{row.returnFlowLogistic !== 0 ? (
					<FlowLine
						label="Логистика возвратов"
						value={row.returnFlowLogistic}
						unitValue={perUnit(row.returnFlowLogistic)}
						showPerUnit={showPerUnit}
					/>
				) : null}
				{row.crossDock !== 0 ? (
					<FlowLine
						label="Кросс-докинг"
						value={row.crossDock}
						unitValue={perUnit(row.crossDock)}
						showPerUnit={showPerUnit}
					/>
				) : null}
				<FlowLine
					label="Продвижение и реклама"
					value={row.promotion}
					unitValue={perUnit(row.promotion)}
					showPerUnit={showPerUnit}
				/>
				{row.premiumSubscription !== 0 ? (
					<FlowLine
						label="Подписка Premium"
						value={row.premiumSubscription}
						unitValue={perUnit(row.premiumSubscription)}
						showPerUnit={showPerUnit}
					/>
				) : null}
				<FlowLine
					label="Прочие начисления"
					value={row.other}
					unitValue={perUnit(row.other)}
					showPerUnit={showPerUnit}
					onLabelClick={
						reportingPeriod ? () => setOtherAccrualsOpen(true) : undefined
					}
				/>
				<FlowLine
					label="Итоговая прибыль"
					value={row.totalProfit}
					unitValue={perUnit(row.totalProfit)}
					total
					showPerUnit={showPerUnit}
				/>
				<FlowLine
					label="COGS"
					value={row.cogs}
					unitValue={perUnit(row.cogs)}
					asExpense
					showPerUnit={showPerUnit}
				/>
				<FlowLine
					label="Налог"
					value={row.tax}
					unitValue={perUnit(row.tax)}
					asExpense
					showPerUnit={showPerUnit}
				/>
			</CardContent>

			<CardFooter className="flex w-full flex-col items-stretch gap-2 border-t bg-muted/50 p-3">
				<FlowLine
					label="Чистая прибыль"
					value={row.netProfit}
					unitValue={perUnit(row.netProfit)}
					emphasis
					showPerUnit={showPerUnit}
				/>
				<FlowLine
					label="ROI"
					value={row.roiPercent}
					unitValue={row.roiPercent}
					percent
					showPerUnit={showPerUnit}
				/>
			</CardFooter>
		</Card>
		{reportingPeriod ? (
			<StoreOtherAccrualsDialog
				open={otherAccrualsOpen}
				onOpenChange={setOtherAccrualsOpen}
				from={reportingPeriod.from}
				to={reportingPeriod.to}
			/>
		) : null}
		</>
	);
}
